import * as policyRimBusinessLogic from '../../business/logic/policy-business-logic-rim';
import * as policySvsBusinessLogic from '../../business/logic/policy-business-logic-svs';
import * as sharedApi from '../utils/api/sharedApi';
import * as validation from '../../business/logic/shared-validation';

import { ConfirmationModal, createConfirmationModal, ToastMessageType } from './model';
import { addRimLine, changeViewBasic as changeViewBasicRim, changeView as changeViewRim, removeRimLine, rimLineItemChange, rimLineSectionChange, rimLineVatThresholdChange, setValidated as setRimValidated, validateRim } from '../details/rim/actions';
import { all, call, fork, put, select, take, takeEvery, cancel } from 'redux-saga/effects';
import { businessUpdate, getDownloadLink, getDownloadLinkSuccess, getDownloadLinkFail, hideConfirmationModal, setCurrentUserInfo, setToastMessage, showConfirmationModal, toggleIsUIDisabled, validateEmail } from './actions';
import { changeView, changeViewBasic, setValidated as setSvsValidated, tabValueAndCauseFieldChange, validateSvs } from '../details/svs/actions';
import { mapStateToPolicyBusiness, rimCurrentView, svsCurrentView } from './selectors';

// import { AppInsights } from 'applicationinsights-js';
import { BusinessResultType } from '../../business/models/policy-business.model';
import { CaseActionTypes } from '../case/types';
import { DaccaActionTypes } from '../details/dacca/types';
import { DetailsAgendaActionTypes } from '../details/agenda/types';
import { DetailsCaseActionTypes } from '../details/case/types';
import { DetailsCommentOrReportActionTypes } from '../details/comment-or-report/types';
import { DetailsExpertiseAttachmentActionTypes } from '../details/expertise-attachment/types';
import { DetailsHonoraryNoteActionTypes } from '../details/honorary-note/types';
import { DetailsImagesActionTypes } from '../details/images/types';
import { DetailsInsuredPersonActionTypes } from '../details/insured-person/types';
import { DetailsOtherAttachmentsActionTypes } from '../details/other-attachments/types';
import { DetailsPolicyActionTypes } from '../details/policy/types';
import { DetailsRimActionTypes } from '../details/rim/types';
import { DetailsSvsActionTypes } from '../details/svs/types';
import { LoginActionTypes } from '../login/types';
import { OverviewActionTypes } from '../details/overview/types';
import { SharedActionTypes } from './types';
import { getI18n } from '../utils/i18n/strings.manager';
import { toast } from 'react-toastify';
import { navigateTo } from '../case/actions';
import { SagaIterator } from 'redux-saga';

function* handleUserInfoChange(action: ReturnType<typeof setCurrentUserInfo>): SagaIterator {
  yield call(() => {
    const localization = getI18n();
    localization.setLanguage(action.payload.currentLanguage);
  });
}

function* handleSetToastMessage(action: ReturnType<typeof setToastMessage>): SagaIterator {
  yield call(() => {
    if (action.payload.message) {
      const message = action.payload.message.messages.join('\n');
      if (action.payload.message.type === ToastMessageType.DEFAULT) {
        toast(message);
      } else {
        toast[action.payload.message.type.toString().toLowerCase()](message);
      }
    } else {
      toast.dismiss();
    }
  });
}

function* handleValidationAddLines(action: ReturnType<any>): SagaIterator {
  const policyForValidation = yield select(mapStateToPolicyBusiness);
  const validationResult = policySvsBusinessLogic.businessCheckSvsLines(policyForValidation);

  if (validationResult.resultType !== BusinessResultType.ok) {
    yield call(withConfirmation, validationResult.messages, '', '', function* (): SagaIterator {
      yield put(businessUpdate(validationResult));
    });
  } else {
    yield put(businessUpdate(validationResult));
  }
}

function* handleValidationRemoveLine(action: ReturnType<any>): SagaIterator {
  const policyForValidation = yield select(mapStateToPolicyBusiness);
  const validationResult = policySvsBusinessLogic.businessCheckSvsLines(policyForValidation);

  // update cause of total
  yield put(businessUpdate(validationResult));
}


function* handleChangeSvsTab(action: ReturnType<typeof changeViewBasic>): SagaIterator {
  const currentView = yield (select(svsCurrentView));

  if (currentView === action.payload.view) { return; }

  yield put(changeView(action.payload));
}

function* handleValueAndCauseValidity(action: ReturnType<typeof tabValueAndCauseFieldChange>): SagaIterator {
  yield call(checkDamageCauseRemark);
}

function* checkDamageCauseRemark(): SagaIterator {
  const policyForValidation = yield select(mapStateToPolicyBusiness);
  const validationResult = policySvsBusinessLogic.businessCheckSvsIsDamageCauseValid(policyForValidation);

  // update cause of damage cause validation
  yield put(businessUpdate(validationResult));
}

function* handleFetchSvsValidity(): SagaIterator {
  const policyForValidation = yield select(mapStateToPolicyBusiness);
  const validationResult = policySvsBusinessLogic.businessCheckSvsIsValid(policyForValidation);

  // or compose sagas, send result.data of previous saga into next saga!?
  // update svs validation
  yield put(businessUpdate(validationResult));
}

function* handleChangeRimTab(action: ReturnType<typeof changeViewBasicRim>): SagaIterator {
  const currentView = yield (select(rimCurrentView));

  if (currentView === action.payload.view) { return; }

  yield put(changeViewRim(action.payload));
}

function* checkExcessApplicableAndUnderConditionValidity(): SagaIterator {
  const policyForValidation = yield select(mapStateToPolicyBusiness);
  const validationResult = policySvsBusinessLogic.businessCheckSvsExcessApplicableAndUnderConditionTC(policyForValidation);

  // update excess applicable and under condition validation
  yield put(businessUpdate(validationResult));
}

export function* emptySaga(): IterableIterator<any> { }

export function* withConfirmation(messages: string[], okText: string, cancelText: string, onConfirm: () => SagaIterator, onCancel = emptySaga): SagaIterator {
  const modal: ConfirmationModal = createConfirmationModal();

  const localization = getI18n();

  modal.messages = messages.map((message) => localization.get(message));
  modal.okText = okText;
  modal.cancelText = cancelText;

  yield put(toggleIsUIDisabled(false));
  yield put(showConfirmationModal(modal));

  const { type } = yield take([
    SharedActionTypes.CONFIRMATION_MODAL_OK,
    SharedActionTypes.CONFIRMATION_MODAL_CANCEL
  ]);

  switch (type) {
    case SharedActionTypes.CONFIRMATION_MODAL_OK:
      yield call(onConfirm);
      break;

    case SharedActionTypes.CONFIRMATION_MODAL_CANCEL:
      yield call(onCancel);
      break;

    default:
      throw new Error(`${type} - Missing implementation`);
  }

  yield put(hideConfirmationModal());
}

export function* withConfirmationTranslated(translatedMessages: string[], okText: string, cancelText: string, onConfirm: () => SagaIterator, onCancel = emptySaga): SagaIterator {
  const modal: ConfirmationModal = createConfirmationModal();

  modal.messages = translatedMessages;
  modal.okText = okText;
  modal.cancelText = cancelText;

  yield put(toggleIsUIDisabled(false));
  yield put(showConfirmationModal(modal));

  const { type } = yield take([
    SharedActionTypes.CONFIRMATION_MODAL_OK,
    SharedActionTypes.CONFIRMATION_MODAL_CANCEL
  ]);

  switch (type) {
    case SharedActionTypes.CONFIRMATION_MODAL_OK:
      yield call(onConfirm);
      break;

    case SharedActionTypes.CONFIRMATION_MODAL_CANCEL:
      yield call(onCancel);
      break;

    default:
      throw new Error(`${type} - Missing implementation`);
  }

  yield put(hideConfirmationModal());
}

function* handleRimSectionChange(action: ReturnType<typeof rimLineSectionChange>): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policyRimBusinessLogic.businessRimSectionChange(policyBusiness);
  yield put(businessUpdate(result));
}

function* handleRimItemChange(action: ReturnType<typeof rimLineItemChange>): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policyRimBusinessLogic.businessRimItemChange(policyBusiness);
  yield put(businessUpdate(result));
}

function* handleRimVatThresholdChange(action: ReturnType<typeof rimLineVatThresholdChange>): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policyRimBusinessLogic.businessRimVatThresholdChange(policyBusiness);
  yield put(businessUpdate(result));
}

function* handleAddRimLine(action: ReturnType<typeof addRimLine>): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policyRimBusinessLogic.businessAddRimLine(policyBusiness);

  if (result.resultType !== BusinessResultType.ok) {
    yield call(withConfirmation, result.messages, '', '', function* (): SagaIterator {
      yield put(businessUpdate(result));
    });
  } else {
    yield put(businessUpdate(result));
  }
}

function* handleRemoveRimLine(action: ReturnType<typeof removeRimLine>): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policyRimBusinessLogic.businessRemoveRimLine(policyBusiness, action.payload);
  
  if (result.resultType !== BusinessResultType.ok) {
    yield call(withConfirmation, result.messages, '', '', function* (): SagaIterator {
      yield put(businessUpdate(result));
    });
  } else {
    yield put(businessUpdate(result));
  }
}

function* handleValidateRim(action: ReturnType<typeof validateRim>): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policyRimBusinessLogic.validateRim(policyBusiness);

  if (result.resultType !== BusinessResultType.ok) {
    yield call(withConfirmation, result.messages, '', '', function* (): SagaIterator {
      yield put(setRimValidated(false));
    });
  } else {
    yield put(setRimValidated(true));
  }
}

function* handleValidateSvs(action: ReturnType<typeof validateSvs>): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policySvsBusinessLogic.businessCheckSvsIsValid(policyBusiness);

  if (result.resultType !== BusinessResultType.ok) {
    yield call(withConfirmation, result.messages, '', '', function* (): SagaIterator {
      yield put(setSvsValidated(false));
    });
  } else {
    yield put(setSvsValidated(true));
  }
}

function* handleUpdateRimIBAN(): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policyRimBusinessLogic.businessUpdateRimIBAN(policyBusiness);
  yield put(businessUpdate(result));
}

function* handleUpdateRimBBAN(): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policyRimBusinessLogic.businessUpdateRimBBAN(policyBusiness);

  if (result.resultType !== BusinessResultType.ok) {
    yield call(withConfirmation, result.messages, '', '', function* (): SagaIterator {
      yield put(businessUpdate(result));
    });
  }
}

function* handleUpdateSvsIBAN(): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policySvsBusinessLogic.businessUpdateSvsIBAN(policyBusiness);
  yield put(businessUpdate(result));
}

function* handleUpdateSvsBBAN(): SagaIterator {
  const policyBusiness = yield select(mapStateToPolicyBusiness);
  const result = policySvsBusinessLogic.businessUpdateSvsBBAN(policyBusiness);

  if (result.resultType !== BusinessResultType.ok) {
    yield call(withConfirmation, result.messages, '', '', function* (): SagaIterator {
      yield put(businessUpdate(result));
    });
  } else {
    yield put(businessUpdate(result));
  }
}

function* handleValidateEmail(action: ReturnType<typeof validateEmail>): SagaIterator {
  if (action.payload.email) {
    const result = validation.isValidEmail(action.payload.email);

    if (result.resultType !== BusinessResultType.ok) {
      yield call(withConfirmation, result.messages, '', '', function* (): SagaIterator {
        yield call(emptySaga);
      });
    }
  }
}

function* handleApiError(action: any): SagaIterator {
  // log to ApplicationInsights
  // AppInsights.trackException(new Error(JSON.stringify(action))); // to stringify !?

  if (action.payload && action.payload.code === 404) {
    yield put(navigateTo('/not-found'));
    yield cancel();
  }

  return true;
}

function* handleGetDownloadLink(action: ReturnType<typeof getDownloadLink>): SagaIterator {
  if (action.payload.amazonKey) {
    const result = yield call(sharedApi.getDownloadLink, action.payload.amazonKey, action.payload.bucketName);

    if (!result) {
      yield put(getDownloadLinkFail(JSON.stringify(result.response)));
    } else {
      yield put(
        getDownloadLinkSuccess({
          amazonKey: action.payload.amazonKey,
          url: result.url,
          filename: result.filename, // Include the filename
        })
      ); // Success with the URL
    }
  } else if (action.payload.url) {
    yield put(
      getDownloadLinkSuccess({
        amazonKey: action.payload.amazonKey,
        url: action.payload.url,
        filename: action.payload.filename, // Include the filename
      })
    ); // Handle already-provided URLs
  }
}

function* watchValidation() {
  yield takeEvery(DetailsSvsActionTypes.TAB_LINES_ADD_LINE, handleValidationAddLines);
  yield takeEvery(DetailsSvsActionTypes.TAB_LINES_REMOVE_LINE, handleValidationRemoveLine);
  yield takeEvery(DetailsSvsActionTypes.CHANGE_VIEW_BASIC, handleChangeSvsTab);
  yield takeEvery(DetailsRimActionTypes.CHANGE_VIEW_BASIC, handleChangeRimTab);
  yield takeEvery(DetailsRimActionTypes.RIM_LINE_SECTION_CHANGE, handleRimSectionChange);
  yield takeEvery(DetailsRimActionTypes.RIM_LINE_ITEM_CHANGE, handleRimItemChange);
  yield takeEvery(DetailsRimActionTypes.RIM_LINE_VAT_THRESHOLD_CHANGE, handleRimVatThresholdChange);
  yield takeEvery(DetailsRimActionTypes.ADD_RIM_LINE, handleAddRimLine);
  yield takeEvery(DetailsRimActionTypes.REMOVE_RIM_LINE, handleRemoveRimLine);
  yield takeEvery(DetailsSvsActionTypes.TAB_VALUE_AND_CAUSE_FIELD_CHANGE, handleValueAndCauseValidity);
  yield takeEvery(DetailsSvsActionTypes.TAB_LINES_SET_EXCESS_APPLICABLE, checkExcessApplicableAndUnderConditionValidity);
  yield takeEvery(DetailsSvsActionTypes.TAB_LINES_SET_UNDER_CONDITION, checkExcessApplicableAndUnderConditionValidity);
  yield takeEvery(DetailsSvsActionTypes.FETCH_SVS_VALIDATE, handleFetchSvsValidity);
  yield takeEvery(DetailsRimActionTypes.VALIDATE_RIM, handleValidateRim);
  yield takeEvery(DetailsSvsActionTypes.VALIDATE_SVS, handleValidateSvs);
  yield takeEvery(DetailsRimActionTypes.UPDATE_RIM_IBAN, handleUpdateRimIBAN);
  yield takeEvery(DetailsRimActionTypes.UPDATE_RIM_BBAN, handleUpdateRimBBAN);
  yield takeEvery(DetailsSvsActionTypes.UPDATE_SVS_IBAN, handleUpdateSvsIBAN);
  yield takeEvery(DetailsSvsActionTypes.UPDATE_SVS_BBAN, handleUpdateSvsBBAN);
  yield takeEvery(SharedActionTypes.VALIDATE_EMAIL, handleValidateEmail);
}

function* watchApiErrors() {
  yield takeEvery(CaseActionTypes.FETCH_CASES_FAIL, handleApiError);
  yield takeEvery(CaseActionTypes.REFUSE_CASE_FAIL, handleApiError);
  yield takeEvery(CaseActionTypes.ARCHIVE_CASE_FAIL, handleApiError);
  yield takeEvery(CaseActionTypes.DEARCHIVE_CASE_FAIL, handleApiError);
  yield takeEvery(CaseActionTypes.FETCH_CASE_TYPECODES_FAIL, handleApiError);
  yield takeEvery(CaseActionTypes.ASSIGN_USER_OPEN_FAIL, handleApiError);
  yield takeEvery(CaseActionTypes.ASSIGN_USER_FAIL, handleApiError);
  yield takeEvery(CaseActionTypes.FETCH_CASES_COUNT_FAIL, handleApiError);
  yield takeEvery(CaseActionTypes.MARK_AS_READ_FAIL, handleApiError);

  yield takeEvery(LoginActionTypes.LOG_IN_FAIL, handleApiError);

  yield takeEvery(DetailsAgendaActionTypes.FETCH_AGENDA_FAIL, handleApiError);
  yield takeEvery(DetailsAgendaActionTypes.UPDATE_AGENDA_FAIL, handleApiError);

  yield takeEvery(DetailsCaseActionTypes.FETCH_CASE_VIEW_FAIL, handleApiError);

  yield takeEvery(DetailsCommentOrReportActionTypes.FETCH_COMMENT_OR_REPORT_FAIL, handleApiError);
  yield takeEvery(DetailsCommentOrReportActionTypes.UPLOAD_COMMENT_OR_REPORT_FAIL, handleApiError);
  yield takeEvery(DetailsCommentOrReportActionTypes.DELETE_COMMENT_OR_REPORT_FAIL, handleApiError);
  yield takeEvery(DetailsCommentOrReportActionTypes.FETCH_DOCUMENT_TYPES_FAIL, handleApiError);

  yield takeEvery(DaccaActionTypes.IMPORT_LINES_FROM_DACCA_FAIL, handleApiError);
  yield takeEvery(DaccaActionTypes.FETCH_DACCA_CALCULATIONS_FAIL, handleApiError);
  yield takeEvery(DaccaActionTypes.FETCH_DACCA_TOKEN_FAIL, handleApiError);

  yield takeEvery(DetailsExpertiseAttachmentActionTypes.FETCH_EXPERTISE_ATTACHMENTS_FAIL, handleApiError);

  yield takeEvery(DetailsHonoraryNoteActionTypes.FETCH_HONORARY_NOTES_FAIL, handleApiError);
  yield takeEvery(DetailsHonoraryNoteActionTypes.FETCH_HONORARY_NOTE_TYPECODES_FAIL, handleApiError);
  yield takeEvery(DetailsHonoraryNoteActionTypes.SAVE_HONORARY_NOTE_FAIL, handleApiError);
  yield takeEvery(DetailsHonoraryNoteActionTypes.ADD_HONORARY_NOTE_LINE_FAIL, handleApiError);
  yield takeEvery(DetailsHonoraryNoteActionTypes.DELETE_HONORARY_NOTE_FAIL, handleApiError);
  yield takeEvery(DetailsHonoraryNoteActionTypes.DELETE_HONORARY_NOTE_LINE_FAIL, handleApiError);

  yield takeEvery(DetailsImagesActionTypes.FETCH_IMAGES_FAIL, handleApiError);
  yield takeEvery(DetailsImagesActionTypes.UPLOAD_IMAGES_FAIL, handleApiError);
  yield takeEvery(DetailsImagesActionTypes.DELETE_IMAGES_FAIL, handleApiError);
  yield takeEvery(DetailsImagesActionTypes.UPDATE_IMAGES_FAIL, handleApiError);

  yield takeEvery(DetailsInsuredPersonActionTypes.FETCH_INSURED_PERSON_FAIL, handleApiError);

  yield takeEvery(DetailsOtherAttachmentsActionTypes.FETCH_OTHER_ATTACHMENTS_FAIL, handleApiError);
  yield takeEvery(DetailsOtherAttachmentsActionTypes.UPLOAD_OTHER_ATTACHMENTS_FAIL, handleApiError);
  yield takeEvery(DetailsOtherAttachmentsActionTypes.DELETE_OTHER_ATTACHMENTS_FAIL, handleApiError);

  yield takeEvery(OverviewActionTypes.SEND_ITEMS_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.DELETE_ITEMS_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.GET_RIM_LIST_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.GET_SVS_LIST_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.GET_DOCUMENT_LIST_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.GET_HONORARY_NOTE_LIST_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.GET_ASKED_AMOUNT_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.SET_ASKED_AMOUNT_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.RIM_STATUS_CHANGE_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.GENERATE_REPORTS_RIM_FAIL, handleApiError);
  yield takeEvery(OverviewActionTypes.GENERATE_REPORTS_SVS_FAIL, handleApiError);

  yield takeEvery(DetailsPolicyActionTypes.FETCH_POLICY_VIEW_MODEL_FAIL, handleApiError);

  yield takeEvery(DetailsRimActionTypes.FETCH_RIM_FAIL, handleApiError);
  yield takeEvery(DetailsRimActionTypes.FETCH_RIMS_TOTAL_FAIL, handleApiError);
  yield takeEvery(DetailsRimActionTypes.FETCH_RIMS_TYPECODES_FAIL, handleApiError);

  yield takeEvery(DetailsSvsActionTypes.FETCH_SVS_LIST_FAIL, handleApiError);
  yield takeEvery(DetailsSvsActionTypes.FETCH_SVS_FAIL, handleApiError);
}

function* watchShared() {
  yield takeEvery(SharedActionTypes.SET_CURRENT_USER_INFO, handleUserInfoChange);
  yield takeEvery(SharedActionTypes.SET_TOAST_MESSAGE, handleSetToastMessage);
  yield takeEvery(SharedActionTypes.GET_DOWNLOAD_LINK, handleGetDownloadLink);
}

function* sharedSaga() {
  yield all([fork(watchShared), fork(watchValidation), fork(watchApiErrors)]);
}

export default sharedSaga;
